/**
 * Adaptador para convertir datos de PersonForm a formato compatible con API PHP
 * Convierte base64 a File objects y estructura los datos correctamente
 */

// Documentos individuales del colaborador (reemplaza el "Adjuntos" genérico
// de antes). Misma lista usada por PersonForm.jsx (para renderizar los
// campos) y por server/api/people.php (mismas claves exactas en
// $documentFields) — si se agrega/quita un documento hay que actualizar
// los tres lugares.
export const DOCUMENT_FIELD_DEFS = [
  { key: 'docCedula', label: 'Cédula' },
  { key: 'docLicenciaConduccion', label: 'Licencia de conducción' },
  { key: 'docTarjetaProfesional', label: 'Tarjeta profesional' },
  { key: 'docCertificadoEducativo', label: 'Certificado educativo' },
  { key: 'docCertificadosLaborales', label: 'Certificados laborales' },
  { key: 'docCertificadoAlturas', label: 'Certificado de alturas' },
  { key: 'docCertificadoManejoDefensivo', label: 'Certificado manejo defensivo' },
  { key: 'docCertificadoMecanicaBasica', label: 'Certificado mecánica básica' },
  { key: 'docCertificadoAparejador', label: 'Certificado de aparejador' },
  { key: 'docCertificadoRedes1164', label: 'Certificado mantener redes 1164' },
  { key: 'docCertificadoIluminacion1054', label: 'Certificado sistemas de iluminación 1054 (nivel intermedio)' },
  { key: 'docCertificadoIluminacion1053', label: 'Certificado sistemas de iluminación 1053 (nivel básico)' },
  { key: 'docCertificadoOperadorGrua', label: 'Certificado operador de grúa' },
  { key: 'docCertificadoOperadorCanasta', label: 'Certificado operador de canasta' },
  { key: 'docCertificadoPodas', label: 'Certificado de podas' },
  { key: 'docCertificadoCoordinadorAlturas', label: 'Certificado coordinador de alturas' },
  { key: 'docCertificado50HorasSst', label: 'Certificado 50 horas SST' },
  { key: 'docCertificado20HorasSst', label: 'Certificado 20 horas SST' },
  { key: 'docValidacionCliente', label: 'Validación por cliente' },
  { key: 'docEvidenciaInduccion', label: 'Evidencia de inducción' },
  { key: 'docExamenMedicoIngreso', label: 'Examen médico de ingreso' },
  { key: 'docExamenMedicoPeriodico', label: 'Examen médico periódico' },
  { key: 'docExamenMedicoPosIncapacidad', label: 'Examen médico pos incapacidad' },
  { key: 'docExamenMedicoEgreso', label: 'Examen médico de egreso' },
  { key: 'docCertificadoTrabajoConfinado', label: 'Certificado de trabajo confinado' },
  { key: 'docTarjetaConte', label: 'Tarjeta CONTE' },
  { key: 'docExamenPsicosensometrico', label: 'Examen médico psicosensométrico' },
  { key: 'docCertificadoSustanciasQuimicas', label: 'Certificado transporte de sustancias químicas' },
  { key: 'docCertificadoPrimerosAuxilios', label: 'Certificado de primeros auxilios' },
  { key: 'docCertificadoVacunas', label: 'Certificado de vacunas' },
  { key: 'docResolucion', label: 'Resolución' },
  { key: 'docOtrosDocumentos', label: 'Otros documentos' },
];
const DOCUMENT_FIELDS = DOCUMENT_FIELD_DEFS.map((d) => d.key);

/**
 * Convierte una data URL (base64) a un objeto File
 * @param {string} dataUrl - Data URL en formato base64
 * @param {string} filename - Nombre del archivo
 * @returns {File} Objeto File
 */
function dataURLtoFile(dataUrl, filename) {
  if (!dataUrl || typeof dataUrl !== 'string') return null;
  
  try {
    const arr = dataUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'application/octet-stream';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    
    return new File([u8arr], filename, { type: mime });
  } catch (error) {
    console.error('Error convirtiendo dataURL a File:', error);
    return null;
  }
}

/**
 * Convierte los datos del PersonForm a FormData para enviar a PHP
 * @param {Object} formData - Datos del formulario de PersonForm
 * @returns {FormData} FormData listo para enviar
 */
export function personFormToAPI(formData) {
  const apiData = new FormData();
  
  // Campos de texto simples - mapeo directo
  const textFields = {
    name: formData.name,
    position: formData.position,
    area: formData.area,
    clientValidation: formData.clientValidation,
    phone: formData.phone,
    civilStatus: formData.civilStatus,
    email: formData.email,
    idIssuePlace: formData.idIssuePlace,
    category: formData.category,
    appliesExtras: formData.appliesExtras,
    eps: formData.eps,
    compensationFund: formData.compensationFund,
    drivingLicense: formData.drivingLicense,
    drivingLicenseExpiry: formData.drivingLicenseExpiry,
    pensionFund: formData.pensionFund,
    afp: formData.afp,
    bloodType: formData.bloodType,
    allergies: formData.allergies,
    allergyType: formData.allergyType,
    birthCity: formData.birthCity,
    birthDate: formData.birthDate,
    address: formData.address,
    neighborhood: formData.neighborhood,
    municipality: formData.municipality,
    department: formData.department,
    hireDate: formData.hireDate,
    education: formData.education,
    contractDate: formData.contractDate,
    contractNumber: formData.contractNumber,
    currentContractModality: formData.currentContractModality,
    // Solo se envía si tiene valor; el servidor lo ignora si quien guarda
    // no tiene permiso y, si no llega, conserva el salario anterior.
    salary: formData.salary,
    immediateBossId: formData.immediateBossId,
    contractEndDate: formData.contractEndDate,
    arlRiskPercent: formData.arlRiskPercent,
    emoDate: formData.emoDate,
    nextEmoDate: formData.nextEmoDate,
    status: formData.status,
  };
  
  // Agregar campos de texto al FormData
  Object.entries(textFields).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      apiData.append(key, String(value));
    }
  });

  // Siempre enviar rol operativo (vacío permite quitarlo en people.php).
  apiData.append('operationalRole', String(formData.operationalRole ?? ''));

  const dbId = formData?._dbId ?? formData?.dbId ?? null;
  if (dbId !== null && dbId !== undefined && String(dbId).trim() !== '') {
    apiData.append('id', String(dbId));
  }

  const dniRaw = formData?.dni ?? formData?.documento_identidad ?? formData?.id ?? '';
  const dniNorm = String(dniRaw).replace(/\D/g, '');
  if (dniNorm) {
    if (!apiData.has('dni')) apiData.append('dni', dniNorm);
    if (!apiData.has('documento_identidad')) apiData.append('documento_identidad', dniNorm);
  }
  
  // Categorías de Tarjeta CONTE (T1-T6, AUX DE INGENIERIA) - selección múltiple.
  // Siempre se envía (incluso vacío) para que desmarcar todas quede
  // guardado — omitirlo cuando queda vacío haría que el backend conserve
  // el valor anterior en vez de limpiarlo.
  if (Array.isArray(formData.conteCategories)) {
    apiData.append('conteCategories', JSON.stringify(formData.conteCategories));
  }

  // Licencias de conducción con vencimiento individual ([{category, expiry}])
  // — mismo criterio: siempre se envía para permitir limpiar.
  if (Array.isArray(formData.drivingLicenses)) {
    apiData.append('drivingLicenses', JSON.stringify(formData.drivingLicenses));
  }

  // Tarjetas y consejos profesionales (CONALTEL, COPNIA, etc.) — selección
  // múltiple, mismo criterio que conteCategories.
  if (Array.isArray(formData.professionalCouncils)) {
    apiData.append('professionalCouncils', JSON.stringify(formData.professionalCouncils));
  }

  // Experiencia laboral previa ([{company, position, startDate, endDate}])
  // — mismo criterio: siempre se envía para permitir limpiar.
  if (Array.isArray(formData.workExperience)) {
    apiData.append('workExperience', JSON.stringify(formData.workExperience));
  }

  // Dosis de vacunas aplicadas (Hepatitis A/B, Tétano, Fiebre amarilla,
  // COVID-19) — mismo criterio: siempre se envía para permitir limpiar.
  if (formData.vaccineDoses && typeof formData.vaccineDoses === 'object') {
    apiData.append('vaccineDoses', JSON.stringify(formData.vaccineDoses));
  }

  // Contactos de emergencia (primer y segundo respondiente) — mismo
  // criterio: siempre se envían para permitir limpiar.
  if (formData.firstResponder && typeof formData.firstResponder === 'object') {
    apiData.append('firstResponder', JSON.stringify(formData.firstResponder));
  }
  if (formData.secondResponder && typeof formData.secondResponder === 'object') {
    apiData.append('secondResponder', JSON.stringify(formData.secondResponder));
  }

  // Manejar otrosí: el backend espera 'otrosiDates' (array o JSON) y deriva 'otrosiDate'
  if (Array.isArray(formData.otrosiDates) && formData.otrosiDates.length > 0) {
    const cleanDates = formData.otrosiDates.filter(Boolean);
    const lastOtrosi = cleanDates[cleanDates.length - 1];
    // Compatibilidad: enviar el último como campo singular
    if (lastOtrosi) apiData.append('otrosiDate', lastOtrosi);
    // Enviar todos como 'otrosiDates' en JSON (el backend acepta string JSON o lista separada)
    apiData.append('otrosiDates', JSON.stringify(cleanDates));
  } else if (formData.otrosiDate) {
    apiData.append('otrosiDate', formData.otrosiDate);
  }
  
  // FOTO - Convertir de base64 a File
  if (formData.photo) {
    if (formData.photo.startsWith('data:')) {
      // Es base64, convertir a File
      const photoFile = dataURLtoFile(formData.photo, 'photo.jpg');
      if (photoFile) {
        apiData.append('photo', photoFile);
      }
    } else if (formData.photo instanceof File) {
      // Ya es un File
      apiData.append('photo', formData.photo);
    } else if (typeof formData.photo === 'string' && formData.photo.startsWith('http')) {
      // Es una URL (foto existente) - no enviar nada, PHP la conservará
    }
  }
  
  // FIRMA DIGITAL - Convertir de base64 a File
  if (formData.signature) {
    if (formData.signature.startsWith('data:')) {
      const signatureFile = dataURLtoFile(formData.signature, 'signature.png');
      if (signatureFile) {
        apiData.append('signature', signatureFile);
      }
    } else if (formData.signature instanceof File) {
      apiData.append('signature', formData.signature);
    }
  }
  
  // DOCUMENTOS individuales (cédula, licencia, certificados, etc.) - mismo
  // criterio que foto/firma: cada uno es un solo archivo que se reemplaza
  // al subir uno nuevo; si el valor actual ya es una URL existente, no se
  // reenvía nada y el backend conserva lo que ya estaba.
  DOCUMENT_FIELDS.forEach((key) => {
    const val = formData[key];
    if (!val) return;
    if (typeof val === 'string' && val.startsWith('data:')) {
      const file = dataURLtoFile(val, `${key}.pdf`);
      if (file) apiData.append(key, file);
    } else if (val instanceof File) {
      apiData.append(key, val);
    }
    // string http(s) existente: no se reenvía, el backend la conserva.
  });

  return apiData;
}

/**
 * Convierte respuesta de API PHP a formato compatible con PersonForm
 * @param {Object} apiResponse - Respuesta del servidor PHP
 * @returns {Object} Datos en formato PersonForm
 */
export function apiToPerson(apiResponse) {
  if (!apiResponse) return null;
  
  return {
    id: apiResponse.id,
    name: apiResponse.name,
    position: apiResponse.position,
    operationalRole: apiResponse.operationalRole || apiResponse.operational_role || '',
    area: apiResponse.area,
    clientValidation: apiResponse.clientValidation,
    phone: apiResponse.phone,
    civilStatus: apiResponse.civilStatus,
    email: apiResponse.email,
    eps: apiResponse.eps,
    idIssuePlace: apiResponse.idIssuePlace,
    category: apiResponse.category,
    appliesExtras: apiResponse.appliesExtras,
    conteCategories: Array.isArray(apiResponse.conteCategories) ? apiResponse.conteCategories : [],
    professionalCouncils: Array.isArray(apiResponse.professionalCouncils) ? apiResponse.professionalCouncils : [],
    workExperience: Array.isArray(apiResponse.workExperience) ? apiResponse.workExperience : [],
    vaccineDoses: (apiResponse.vaccineDoses && typeof apiResponse.vaccineDoses === 'object') ? apiResponse.vaccineDoses : {},
    firstResponder: (apiResponse.firstResponder && typeof apiResponse.firstResponder === 'object') ? apiResponse.firstResponder : {},
    secondResponder: (apiResponse.secondResponder && typeof apiResponse.secondResponder === 'object') ? apiResponse.secondResponder : {},
    compensationFund: apiResponse.compensationFund,
    drivingLicense: apiResponse.drivingLicense,
    drivingLicenseExpiry: apiResponse.drivingLicenseExpiry,
    // Migra personas guardadas antes de que existiera drivingLicenses
    // (solo tenían drivingLicense/drivingLicenseExpiry como valor único).
    drivingLicenses: Array.isArray(apiResponse.drivingLicenses)
      ? apiResponse.drivingLicenses
      : (apiResponse.drivingLicense ? [{ category: apiResponse.drivingLicense, expiry: apiResponse.drivingLicenseExpiry || '' }] : []),
    pensionFund: apiResponse.pensionFund,
    afp: apiResponse.afp,
    bloodType: apiResponse.bloodType,
    allergies: apiResponse.allergies,
    allergyType: apiResponse.allergyType,
    birthCity: apiResponse.birthCity,
    birthDate: apiResponse.birthDate,
    address: apiResponse.address,
    neighborhood: apiResponse.neighborhood,
    municipality: apiResponse.municipality,
    department: apiResponse.department,
    hireDate: apiResponse.hireDate || apiResponse.entryDate || apiResponse.fechaIngreso,
    education: apiResponse.education,
    contractDate: apiResponse.contractDate,
    contractNumber: apiResponse.contractNumber,
    currentContractModality: apiResponse.currentContractModality,
    immediateBossId: apiResponse.immediateBossId,
    otrosiDate: apiResponse.otrosiDate,
    otrosiDates: apiResponse.otrosiDates || (apiResponse.otrosiDate ? [apiResponse.otrosiDate] : []),
    arlRiskPercent: apiResponse.arlRiskPercent,
    emoDate: apiResponse.emoDate,
    nextEmoDate: apiResponse.nextEmoDate,
    // Mapear 'estado' (ES) a 'status' (UI) cuando el backend usa columnas en español
    status: apiResponse.status || apiResponse.estado || '',
    photo: apiResponse.photo,
    signature: apiResponse.signature,
    attachments: apiResponse.attachments || [],
    documents: (apiResponse.documents && typeof apiResponse.documents === 'object') ? apiResponse.documents : {},
    _dbId: apiResponse._dbId,
  };
}

/**
 * Hook personalizado para manejar personas con conversión automática
 */
export function usePersonAPI(api) {
  const savePerson = async (formData) => {
    try {
      // Convertir datos del formulario a FormData
      const apiData = personFormToAPI(formData);
      
      // Enviar al API
      const response = await api.people.upsert(apiData);
      
      // Verificar respuesta
      if (response.ok || response.success) {
        return { success: true, message: 'Colaborador guardado exitosamente', data: response };
      } else {
        return { success: false, message: response.error || 'Error al guardar', data: response };
      }
    } catch (error) {
      console.error('Error al guardar colaborador:', error);
      return { success: false, message: error.message || 'Error desconocido', error };
    }
  };
  
  const loadPeople = async (filters = {}) => {
    try {
      const response = await api.people.get(filters);
      
      if (response.ok && Array.isArray(response.data)) {
        // Convertir respuesta de API a formato PersonForm
        return response.data.map(apiToPerson);
      }
      
      return [];
    } catch (error) {
      console.error('Error al cargar colaboradores:', error);
      return [];
    }
  };
  
  const deletePerson = async (id) => {
    try {
      const response = await api.people.delete(id);
      return response.ok || response.success;
    } catch (error) {
      console.error('Error al eliminar colaborador:', error);
      return false;
    }
  };
  
  return {
    savePerson,
    loadPeople,
    deletePerson,
  };
}
